import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Container, 
  Grid, 
  Card, 
  CardContent, 
  CardActions, 
  CardMedia, 
  Button, 
  Chip, 
  Box, 
  Divider, 
  TextField, 
  InputAdornment, 
  Paper,
  Tab,
  Tabs,
  styled,
  useTheme,
  alpha,
  Rating
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import StarIcon from '@mui/icons-material/Star';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import VerifiedIcon from '@mui/icons-material/Verified';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { collection, getDocs, query, where, limit } from 'firebase/firestore';

// Styled components
const PageContainer = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(8),
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  fontWeight: 600,
}));

const FilterBar = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(4),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

const SearchField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const CategoryTabs = styled(Tabs)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const SubscriptionCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
  },
}));

const CardHeader = styled(Box)(({ theme }) => ({
  position: 'relative',
}));

const PopularBadge = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(2),
  right: theme.spacing(2),
  backgroundColor: theme.palette.error.main,
  color: theme.palette.error.contrastText,
  borderRadius: '4px',
  padding: theme.spacing(0.5, 1),
  fontSize: '0.75rem',
  fontWeight: 'bold',
  zIndex: 1,
}));

const PriceTag = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'baseline',
  marginTop: theme.spacing(1),
}));

const Price = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  marginRight: theme.spacing(1),
}));

const Period = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
}));

const BenefitList = styled(Box)(({ theme }) => ({
  margin: theme.spacing(1, 0),
}));

const BenefitChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
  backgroundColor: alpha(theme.palette.primary.main, 0.1),
}));

const OrganizationTitle = styled(Typography)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  marginBottom: theme.spacing(0.5),
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
}));

const RecommendationCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  overflow: 'visible',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: -4,
    left: -4,
    right: -4,
    bottom: -4,
    borderRadius: theme.shape.borderRadius + 4,
    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    zIndex: -1,
    opacity: 0.4,
  },
}));

// Define types for subscription plans and organizations
interface Benefit {
  description: string;
}

interface SubscriptionPlan {
  id: string;
  name: string;
  price: number;
  billingCycle: string;
  description: string;
  benefits: string[];
  popular: boolean;
  category: string;
}

interface Organization {
  id: string;
  name: string;
  logo: string;
  coverImage?: string;
  description: string;
  website?: string;
  location?: string;
  memberCount?: number;
  foundedYear?: number;
  verified: boolean;
  subscriptionPlans: SubscriptionPlan[];
}

// Function to get all categories from organizations
const getAllCategories = (organizations: Organization[]): string[] => {
  const categoriesSet = new Set<string>();
  organizations.forEach(org => {
    org.subscriptionPlans.forEach(plan => {
      if (plan.category) {
        categoriesSet.add(plan.category);
      }
    });
  });
  return Array.from(categoriesSet);
};

// Categories for filtering
const categoryLabels: Record<string, string> = {
  'all': 'All Categories',
  'outdoor': 'Outdoor Activities',
  'travel': 'Travel',
  'creative': 'Creative',
  'food': 'Food & Cuisine',
  'culture': 'Culture & Heritage',
  'fitness': 'Fitness & Wellness'
};

// Mock data for organizations and their subscription plans
const mockOrganizations: Organization[] = [
  {
    id: 'org1',
    name: 'Adventure Seekers',
    logo: 'https://picsum.photos/seed/org1/200/200',
    coverImage: 'https://picsum.photos/seed/org1cover/1200/300',
    description: 'Adventure Seekers connects outdoor enthusiasts with thrilling experiences around the world. From hiking in the Alps to kayaking in the Amazon, we curate unforgettable adventures for all skill levels.',
    website: 'www.adventureseekers.com',
    location: 'Denver, CO',
    memberCount: 12500,
    foundedYear: 2015,
    verified: true,
    subscriptionPlans: [
      {
        id: 'plan1',
        name: 'Explorer',
        price: 9.99,
        billingCycle: 'month',
        description: 'Access to basic hiking trails and local events',
        benefits: ['Local hiking maps', 'Monthly meetups', 'Basic gear guides', '10% discount on local events'],
        popular: false,
        category: 'outdoor',
      },
      {
        id: 'plan2',
        name: 'Adventurer',
        price: 19.99,
        billingCycle: 'month',
        description: 'Full access to all trails and premium events',
        benefits: ['All hiking maps', 'Premium events', 'Gear discounts', 'Route planning', 'Priority registration', 'Exclusive workshops'],
        popular: true,
        category: 'outdoor',
      }
    ]
  },
  {
    id: 'org2',
    name: 'City Explorers',
    logo: 'https://picsum.photos/seed/org2/200/200',
    coverImage: 'https://picsum.photos/seed/org2cover/1200/300',
    description: 'Discover hidden gems in urban environments with City Explorers. Our curated city guides reveal the secrets of metropolitan areas worldwide that most tourists never see.',
    website: 'www.cityexplorers.com',
    location: 'New York, NY',
    memberCount: 8700,
    foundedYear: 2018,
    verified: true,
    subscriptionPlans: [
      {
        id: 'plan3',
        name: 'Urban Traveler',
        price: 12.99,
        billingCycle: 'month',
        description: 'Access to city guides and walking tours',
        benefits: ['City maps', 'Guided tours', 'Local insights', 'Restaurant recommendations'],
        popular: false,
        category: 'travel',
      }
    ]
  },
  {
    id: 'org3',
    name: 'Photography Masters',
    logo: 'https://picsum.photos/seed/org3/200/200',
    coverImage: 'https://picsum.photos/seed/org3cover/1200/300',
    description: 'Learn photography from award-winning professionals. Our instructors have been published in National Geographic, Time, and other prestigious publications.',
    website: 'www.photographymasters.com',
    location: 'San Francisco, CA',
    memberCount: 5600,
    foundedYear: 2016,
    verified: false,
    subscriptionPlans: [
      {
        id: 'plan4',
        name: 'Photography Enthusiast',
        price: 14.99,
        billingCycle: 'month',
        description: 'Learn basic photography techniques',
        benefits: ['Basic tutorials', 'Monthly challenges', 'Community feedback', 'Photo critique sessions'],
        popular: false,
        category: 'creative',
      },
      {
        id: 'plan5',
        name: 'Photography Pro',
        price: 29.99,
        billingCycle: 'month',
        description: 'Advanced photography techniques and exclusive workshops',
        benefits: ['Advanced tutorials', 'Professional feedback', 'Exclusive gear reviews', 'Private workshops', '1-on-1 mentoring sessions'],
        popular: true,
        category: 'creative',
      }
    ]
  },
  {
    id: 'org4',
    name: 'Local Food Explorers',
    logo: 'https://picsum.photos/seed/org4/200/200',
    coverImage: 'https://picsum.photos/seed/org4cover/1200/300',
    description: 'Experience authentic local cuisine around the world. We connect food lovers with authentic culinary experiences, from street food tours to cooking classes with local chefs.',
    website: 'www.localfoodexplorers.com',
    location: 'Chicago, IL',
    memberCount: 7300,
    foundedYear: 2017,
    verified: true,
    subscriptionPlans: [
      {
        id: 'plan6',
        name: 'Food Explorer',
        price: 9.99,
        billingCycle: 'month',
        description: 'Access to local food guides and events',
        benefits: ['Restaurant guides', 'Food events', 'Recipe collections', 'Cooking tips from local chefs'],
        popular: false,
        category: 'food',
      }
    ]
  },
  {
    id: 'org5',
    name: 'Cultural Heritage Tours',
    logo: 'https://picsum.photos/seed/org5/200/200',
    coverImage: 'https://picsum.photos/seed/org5cover/1200/300',
    description: 'Explore the rich cultural heritage of different regions with expert historians and local guides. Our tours bring history to life through immersive experiences.',
    website: 'www.culturalheritagetours.com',
    location: 'Boston, MA',
    memberCount: 9100,
    foundedYear: 2014,
    verified: true,
    subscriptionPlans: [
      {
        id: 'plan7',
        name: 'Cultural Explorer',
        price: 15.99,
        billingCycle: 'month',
        description: 'Access to cultural tours and historical sites',
        benefits: ['Cultural guides', 'Historical insights', 'Virtual tours', 'Member-only events', 'Discounted museum passes'],
        popular: true,
        category: 'culture',
      }
    ]
  }
];

const ExploreSubscriptionsPage = () => {
  const theme = useTheme();
  const { currentUser } = useAuth();
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredOrganizations, setFilteredOrganizations] = useState<Organization[]>([]);
  const [recommendedPlans, setRecommendedPlans] = useState<Array<{plan: SubscriptionPlan, organization: Organization}>>([]);
  const [loading, setLoading] = useState(true);

  // Initialize with mock data
  useEffect(() => {
    // Set filtered organizations to all organizations initially
    setFilteredOrganizations(mockOrganizations);
    
    // Set recommended plans (for demo, we'll use popular plans)
    const recommended = mockOrganizations.flatMap(org => 
      org.subscriptionPlans
        .filter(plan => plan.popular)
        .map(plan => ({ plan, organization: org }))
    );
    
    setRecommendedPlans(recommended);
    setLoading(false);
    
    // In a real app, you would fetch actual data from Firestore here
  }, [currentUser]);

  // Filter organizations based on category and search query
  useEffect(() => {
    // Filter organizations
    let filtered = [...mockOrganizations];
    
    if (activeCategory !== 'all') {
      // Only include organizations that have at least one plan in the selected category
      filtered = filtered.filter(org => 
        org.subscriptionPlans.some(plan => plan.category === activeCategory)
      );
      
      // For each organization, filter its plans to only show those in the selected category
      filtered = filtered.map(org => ({
        ...org,
        subscriptionPlans: org.subscriptionPlans.filter(plan => 
          plan.category === activeCategory
        )
      }));
    }
    
    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(org => 
        org.name.toLowerCase().includes(query) || 
        org.description.toLowerCase().includes(query) ||
        org.subscriptionPlans.some(plan => 
          plan.name.toLowerCase().includes(query) || 
          plan.description.toLowerCase().includes(query)
        )
      );
    }
    
    setFilteredOrganizations(filtered);
  }, [activeCategory, searchQuery]);

  const handleCategoryChange = (event: React.SyntheticEvent, newValue: string) => {
    setActiveCategory(newValue);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const handleSubscribe = (organizationId: string, planId: string) => {
    // This would redirect to purchase page with proper parameters
    console.log(`Subscribe to plan ${planId} from organization ${organizationId}`);
  };

  return (
    <PageContainer maxWidth="lg">
      <SectionTitle variant="h4" gutterBottom>
        Explore Subscriptions
      </SectionTitle>
      
      <FilterBar elevation={1}>
        <SearchField
          fullWidth
          placeholder="Search organizations or subscription plans..."
          variant="outlined"
          size="small"
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        
        <CategoryTabs
          value={activeCategory}
          onChange={handleCategoryChange}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab key="all" label="All Categories" value="all" />
          {getAllCategories(mockOrganizations).map((category) => (
            <Tab key={category} label={categoryLabels[category] || category} value={category} />
          ))}
        </CategoryTabs>
      </FilterBar>
      
      {/* Recommended Section */}
      {recommendedPlans.length > 0 && (
        <>
          <SectionHeader>
            <SectionTitle variant="h5">
              <Box display="flex" alignItems="center">
                <TrendingUpIcon color="primary" sx={{ mr: 1 }} />
                Recommended for You
              </Box>
            </SectionTitle>
          </SectionHeader>
          
          <Grid container spacing={4} sx={{ mb: 6 }}>
            {recommendedPlans.map(({ plan, organization }) => (
              <Grid item xs={12} md={6} lg={4} key={`${organization.id}_${plan.id}`}>
                <RecommendationCard elevation={3}>
                  <CardMedia
                    component="img"
                    height="140"
                    image={organization.logo}
                    alt={organization.name}
                  />
                  <CardContent>
                    <OrganizationTitle variant="subtitle1" color="textSecondary">
                      {organization.name}
                      {organization.verified && (
                        <VerifiedIcon 
                          color="primary" 
                          fontSize="small" 
                          titleAccess="Verified Organization" 
                        />
                      )}
                    </OrganizationTitle>
                    
                    <Typography variant="h6" component="h2" gutterBottom>
                      {plan.name}
                    </Typography>
                    
                    <Typography variant="body2" color="textSecondary" paragraph>
                      {plan.description}
                    </Typography>
                    
                    <PriceTag>
                      <Price variant="h6">${plan.price}</Price>
                      <Period variant="body2">/ {plan.billingCycle}</Period>
                    </PriceTag>
                    
                    <BenefitList>
                      {plan.benefits.map((benefit, index) => (
                        <BenefitChip 
                          key={index} 
                          size="small" 
                          label={benefit} 
                          variant="outlined"
                        />
                      ))}
                    </BenefitList>
                  </CardContent>
                  
                  <Box sx={{ flexGrow: 1 }} />
                  
                  <CardActions>
                    <Button 
                      variant="contained" 
                      color="primary" 
                      fullWidth
                      onClick={() => handleSubscribe(organization.id, plan.id)}
                      component={Link}
                      to={`/subscription-purchase/${organization.id}/${plan.id}`}
                    >
                      Subscribe Now
                    </Button>
                  </CardActions>
                </RecommendationCard>
              </Grid>
            ))}
          </Grid>
        </>
      )}
      
      {/* All Organizations with their subscription plans */}
      <SectionHeader>
        <SectionTitle variant="h5">Organizations & Subscription Plans</SectionTitle>
      </SectionHeader>
      
      {filteredOrganizations.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="textSecondary">
            No organizations found matching your criteria.
          </Typography>
          <Typography variant="body1" color="textSecondary" sx={{ mt: 1 }}>
            Try changing your search terms or category filter.
          </Typography>
        </Paper>
      ) : (
        <>
          {filteredOrganizations.map((organization) => (
            <Box key={organization.id} mb={6}>
              {/* Organization Header */}
              <Paper
                sx={{
                  mb: 3,
                  overflow: 'hidden',
                  position: 'relative',
                  borderRadius: 2,
                }}
                elevation={2}
              >
                {/* Cover Image */}
                <Box
                  sx={{
                    height: 200,
                    position: 'relative',
                    backgroundImage: `url(${organization.coverImage || 'https://picsum.photos/seed/cover/1200/300'})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'rgba(0,0,0,0.4)',
                      zIndex: 1
                    }
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      p: 3,
                      color: 'white',
                      zIndex: 2
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Typography variant="h4" component="h2" sx={{ fontWeight: 'bold', mr: 1 }}>
                        {organization.name}
                      </Typography>
                      {organization.verified && (
                        <VerifiedIcon 
                          color="primary" 
                          fontSize="medium" 
                          titleAccess="Verified Organization" 
                        />
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                      {organization.location && (
                        <Typography variant="body1">
                          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                            <Box component="span" sx={{ mr: 0.5 }}>📍</Box> {organization.location}
                          </Box>
                        </Typography>
                      )}
                      {organization.foundedYear && (
                        <Typography variant="body1">
                          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                            <Box component="span" sx={{ mr: 0.5 }}>🗓️</Box> Est. {organization.foundedYear}
                          </Box>
                        </Typography>
                      )}
                      {organization.memberCount && (
                        <Typography variant="body1">
                          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                            <Box component="span" sx={{ mr: 0.5 }}>👥</Box> {organization.memberCount.toLocaleString()} members
                          </Box>
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
                
                {/* Organization Details */}
                <Box sx={{ p: 3 }}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={3}>
                      <Box 
                        component="img" 
                        src={organization.logo} 
                        alt={organization.name} 
                        sx={{ 
                          width: '100%', 
                          maxWidth: 200, 
                          borderRadius: 2,
                          border: '4px solid white',
                          mt: { xs: 0, md: -8 },
                          position: 'relative',
                          zIndex: 5,
                          boxShadow: 2
                        }} 
                      />
                    </Grid>
                    <Grid item xs={12} md={9}>
                      <Typography variant="body1" paragraph>
                        {organization.description}
                      </Typography>
                      {organization.website && (
                        <Button 
                          variant="text" 
                          color="primary" 
                          startIcon={<Box component="span">🌐</Box>}
                          href={`https://${organization.website}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ mr: 2 }}
                        >
                          Visit Website
                        </Button>
                      )}
                    </Grid>
                  </Grid>
                </Box>
              </Paper>
              
              {/* Subscription Plans */}
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                Available Subscription Plans
              </Typography>
              
              <Grid container spacing={3}>
                {organization.subscriptionPlans.map((plan) => (
                  <Grid item xs={12} sm={6} md={4} key={plan.id}>
                    <SubscriptionCard elevation={2}>
                      <CardHeader>
                        {plan.popular && <PopularBadge>Popular</PopularBadge>}
                      </CardHeader>
                      
                      <CardContent>
                        <Typography variant="h6" component="h2" gutterBottom>
                          {plan.name}
                        </Typography>
                        
                        <Typography variant="body2" color="textSecondary" sx={{ minHeight: 40 }}>
                          {plan.description}
                        </Typography>
                        
                        <PriceTag>
                          <Price variant="h6">${plan.price}</Price>
                          <Period variant="body2">/ {plan.billingCycle}</Period>
                        </PriceTag>
                        
                        <Divider sx={{ my: 1.5 }} />
                        
                        <Typography variant="subtitle2" gutterBottom>
                          Benefits:
                        </Typography>
                        
                        <BenefitList>
                          {plan.benefits.map((benefit, index) => (
                            <BenefitChip 
                              key={index} 
                              size="small" 
                              label={benefit} 
                            />
                          ))}
                        </BenefitList>
                      </CardContent>
                      
                      <Box sx={{ flexGrow: 1 }} />
                      
                      <CardActions sx={{ p: 2 }}>
                        <Button 
                          variant="outlined" 
                          color="primary" 
                          fullWidth
                          onClick={() => handleSubscribe(organization.id, plan.id)}
                          component={Link}
                          to={`/subscription-purchase/${organization.id}/${plan.id}`}
                        >
                          Subscribe
                        </Button>
                      </CardActions>
                    </SubscriptionCard>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))}
        </>
      )}
    </PageContainer>
  );
};

export default ExploreSubscriptionsPage;
